import { useEffect, useCallback, useRef } from 'react';
import { AppNode, Connection, NodeStatus, NodeType } from '../types';
import { unifiedStorage } from '../services/unifiedStorage';
import { useGroupStore } from '../stores/groupStore';
import { useCanvasStore } from '../stores/canvasStore';
import { useTaskStore } from '../stores/taskStore';
import { useNodeStore } from '../stores/nodeStore';
import { config } from '../config';

interface UseDataPersistenceParams {
    isLoaded: boolean;
    dataLoadSucceeded: boolean;
    isInEditor: boolean;
    currentProjectId: string | null;
    nodes: AppNode[];
    connections: Connection[];
    assetHistory: any[];
    workflows: any[];
    setNodes: (nodes: AppNode[]) => void;
    setConnections: (conns: Connection[]) => void;
    setAssetHistory: (assets: any[]) => void;
    setWorkflows: (wfs: any[]) => void;
    setDataLoadSucceeded: (v: boolean) => void;
    setIsLoaded: (v: boolean) => void;
    getThumbnail: () => string;
}

let persistedGroups: any[] = [];

export function getPersistedGroups(): any[] {
    return persistedGroups;
}

export function setPersistedGroups(groups: any[]) {
    persistedGroups = groups;
}

export function nodeHasResult(node: AppNode): boolean {
    const d = node.data;
    if (d.image || (d.images && d.images.length > 0)) return true;
    if (d.videoUri || (d.videoUris && d.videoUris.length > 0)) return true;
    if (d.audioUri) return true;
    if (d.panoramaOutputUrl) return true;
    if (d.analysis) return true;
    return false;
}

function fixAssetPaths(nodes: AppNode[]): AppNode[] {
    return nodes.map(node => {
        const d = node.data;
        let fixed = false;
        const nd = { ...d };
        if (typeof nd.image === 'string' && nd.image.includes('\\')) { nd.image = nd.image.replace(/\\/g, '/'); fixed = true; }
        if (Array.isArray(nd.images)) { const f = nd.images.map((img: string) => typeof img === 'string' && img.includes('\\') ? (fixed = true, img.replace(/\\/g, '/')) : img); if (fixed) nd.images = f; }
        if (typeof nd.videoUri === 'string' && nd.videoUri.includes('\\')) { nd.videoUri = nd.videoUri.replace(/\\/g, '/'); fixed = true; }
        if (Array.isArray(nd.videoUris)) { const f = nd.videoUris.map((vid: string) => typeof vid === 'string' && vid.includes('\\') ? (fixed = true, vid.replace(/\\/g, '/')) : vid); if (fixed) nd.videoUris = f; }
        if (typeof nd.audioUri === 'string' && nd.audioUri.includes('\\')) { nd.audioUri = nd.audioUri.replace(/\\/g, '/'); fixed = true; }
        if (typeof nd.panoramaOutputUrl === 'string' && nd.panoramaOutputUrl.includes('\\')) { nd.panoramaOutputUrl = nd.panoramaOutputUrl.replace(/\\/g, '/'); fixed = true; }
        return fixed ? { ...node, data: nd } : node;
    });
}

function reconcileNodeStatuses(nodes: AppNode[]): AppNode[] {
    const taskStore = useTaskStore.getState();
    const pendingTaskNodeIds = new Set<string>();
    const completedTaskNodeIds = new Set<string>();
    taskStore.pendingTasks.forEach((task) => {
        if (task.status === 'RUNNING') {
            pendingTaskNodeIds.add(task.nodeId);
        } else if (task.status === 'SUCCESS') {
            completedTaskNodeIds.add(task.nodeId);
        }
    });

    return nodes.map((node) => {
        if (node.status === NodeStatus.QUEUED) {
            // 排队仅存在于前端内存队列：刷新后队列丢失，重置 IDLE 待用户手动重新生成
            return { ...node, status: NodeStatus.IDLE, data: { ...node.data, activeFeature: undefined, progress: undefined, pendingTaskId: undefined, taskId: undefined } };
        }
        if (node.status !== NodeStatus.WORKING) {
            if (node.data.activeFeature) {
                return { ...node, data: { ...node.data, activeFeature: undefined } };
            }
            return node;
        }

        if (node.type === NodeType.MEDIA_UPLOAD) {
            return { ...node, status: NodeStatus.IDLE, data: { ...node.data, activeFeature: undefined } };
        }

        // 优先检查 pendingTasks：有正在运行的任务 → 保持 WORKING
        // 这样即使节点 data 里保留着上一次的旧生成结果，也不会被误判为 SUCCESS
        if (pendingTaskNodeIds.has(node.id)) {
            return node;
        }

        // 有 taskId/pendingTaskId 但不在 pendingTasks 中（taskStore 可能未加载或被清空）
        // → 保持 WORKING 并标记 _needsTaskRecovery，由 recoverWorkingNodes 通过后端 API 恢复
        if (node.data.taskId || node.data.pendingTaskId) {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', activeFeature: undefined, _needsTaskRecovery: true } };
        }

        // 无任务标记但有结果 → SUCCESS（真正已完成的节点）
        if (nodeHasResult(node)) {
            return { ...node, status: NodeStatus.SUCCESS, data: { ...node.data, activeFeature: undefined } };
        }

        if (completedTaskNodeIds.has(node.id)) {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '任务已完成，正在恢复结果...', activeFeature: undefined } };
        }

        return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', activeFeature: undefined, _needsTaskRecovery: true } };
    });
}

// 从节点数据中提取所有素材（图片/视频/音频），用于侧边栏历史记录
function extractAssetsFromNodes(nodes: AppNode[]): any[] {
    const assets: any[] = [];
    const seen = new Set<string>();
    for (const node of nodes) {
        const d = node.data as any;
        const title = node.title || '';
        const prompt = typeof d.prompt === 'string' ? d.prompt.trim() : '';
        const push = (type: 'image' | 'video' | 'audio', src: string) => {
            if (!src || seen.has(src)) return;
            seen.add(src);
            assets.push({ id: `a-${node.id}-${type}-${assets.length}`, type, src, title, timestamp: Date.now() - assets.length, prompt: prompt || undefined });
        };
        if (d.image) push('image', d.image);
        if (Array.isArray(d.images)) d.images.forEach((s: string) => push('image', s));
        if (d.videoUri) push('video', d.videoUri);
        if (Array.isArray(d.videoUris)) d.videoUris.forEach((s: string) => push('video', s));
        if (d.audioUri) push('audio', d.audioUri);
    }
    return assets;
}

export function useDataPersistence({
    isLoaded,
    dataLoadSucceeded,
    isInEditor,
    currentProjectId,
    nodes,
    connections,
    assetHistory,
    workflows,
    setNodes,
    setConnections,
    setAssetHistory,
    setWorkflows,
    setDataLoadSucceeded,
    setIsLoaded,
    getThumbnail,
}: UseDataPersistenceParams) {
    const nodesRef = useRef(nodes);
    const connectionsRef = useRef(connections);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDraggingRef = useRef(false);

    useEffect(() => {
        nodesRef.current = nodes;
        connectionsRef.current = connections;
    }, [nodes, connections]);

    useEffect(() => {
        const handleDragStart = () => {
            isDraggingRef.current = true;
        };
        const handleDragStop = () => {
            // P2-2: 延迟设置 isDraggingRef 为 false，让保存逻辑使用更长的延迟
            // 这样拖拽停止后 UI 能先完成渲染，避免保存操作阻塞主线程
            setTimeout(() => {
                isDraggingRef.current = false;
            }, 100);
        };
        window.addEventListener('flow-drag-start', handleDragStart);
        window.addEventListener('flow-drag-stop', handleDragStop);
        return () => {
            window.removeEventListener('flow-drag-start', handleDragStart);
            window.removeEventListener('flow-drag-stop', handleDragStop);
        };
    }, []);

    const loadDataFromStorage = useCallback(async () => {
        // 加载会话开始：飞行期间新建的活节点（WORKING/QUEUED）在 setNodes 覆盖时受保护
        useNodeStore.getState().beginBatchLoad();
        try {
            if (currentProjectId && isInEditor) {
                const canvasData = await unifiedStorage.getCanvas(currentProjectId);
                if (canvasData && canvasData.nodes && canvasData.nodes.length > 0) {
                    const reconciledNodes = reconcileNodeStatuses(fixAssetPaths(canvasData.nodes));
                    setNodes(reconciledNodes);
                    useGroupStore.getState().rebuildGroupsFromNodesData(reconciledNodes);
                    useCanvasStore.getState().rebuildSpatialIndex();
                    // 还原上次的画布视口（原位置、原缩放比例）
                    useCanvasStore.getState().restoreViewport(canvasData.viewport);
                    if (canvasData.edges) setConnections(canvasData.edges);
                    if (canvasData.groups) persistedGroups = canvasData.groups;
                    // 从节点数据反向提取素材填充侧边栏历史记录
                    setAssetHistory(extractAssetsFromNodes(reconciledNodes));
                    setDataLoadSucceeded(true);
                    setIsLoaded(true);
                    return;
                }
            }

            const sAssets = await unifiedStorage.loadFromIndexedDB<any[]>('assets');
            if (sAssets) setAssetHistory(sAssets);
            const sWfs = await unifiedStorage.loadFromIndexedDB<any[]>('workflows');
            if (sWfs) setWorkflows(sWfs);

            if (isInEditor) {
                let sNodes = await unifiedStorage.loadFromIndexedDB<AppNode[]>('nodes');
                let sConns = await unifiedStorage.loadFromIndexedDB<Connection[]>('connections');
                let sGroups = await unifiedStorage.loadFromIndexedDB<any[]>('groups');
                if (!sNodes || sNodes.length === 0) {
                    const backup = await unifiedStorage.loadEmergencyBackup();
                    if (backup.nodes && Array.isArray(backup.nodes) && backup.nodes.length > 0) {
                        console.warn('IndexedDB nodes empty, restoring from emergency localStorage');
                        sNodes = backup.nodes;
                        if (backup.connections) sConns = backup.connections;
                        if (backup.groups) sGroups = backup.groups;
                    }
                }
                if (sNodes && sNodes.length > 0) {
                    const reconciledNodes = reconcileNodeStatuses(fixAssetPaths(sNodes));
                    setNodes(reconciledNodes);
                    useGroupStore.getState().rebuildGroupsFromNodesData(reconciledNodes);
                    useCanvasStore.getState().rebuildSpatialIndex();
                    // 从节点数据反向提取素材，与 IndexedDB 中的历史素材合并去重
                    const nodeAssets = extractAssetsFromNodes(reconciledNodes);
                    const seenSrc = new Set(nodeAssets.map(a => a.src));
                    const merged = [...nodeAssets, ...(sAssets || []).filter(a => !seenSrc.has(a.src))];
                    setAssetHistory(merged);
                }
                if (sConns) setConnections(sConns);
                if (sGroups) persistedGroups = sGroups;
            }

            setDataLoadSucceeded(true);
        } catch (e) {
            console.error("Failed to load storage", e);
        } finally {
            useNodeStore.getState().endBatchLoad();
            setIsLoaded(true);
        }
    }, [currentProjectId, isInEditor]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            const cvState = useCanvasStore.getState();
            const data = {
                nodes: nodesRef.current,
                connections: connectionsRef.current,
                groups: persistedGroups,
                // 刷新前把真实视口一并落盘，刷新后才能原位置还原
                viewport: { x: cvState.x, y: cvState.y, zoom: cvState.zoom },
            };
            unifiedStorage.flush(currentProjectId, data);
            if (currentProjectId && data.nodes && data.nodes.length > 0) {
                try {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', `/api/projects/${currentProjectId}`, false);
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    xhr.send(JSON.stringify({
                        workflow_data: { nodes: data.nodes, connections: data.connections, groups: data.groups },
                    }));
                } catch {}
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentProjectId]);

    useEffect(() => {
        if (!isLoaded) return;
        if (!dataLoadSucceeded && nodes.length === 0) return;

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }

        const doSave = () => {
            unifiedStorage.saveToIndexedDB('assets', assetHistory);
            unifiedStorage.saveToIndexedDB('workflows', workflows);
            unifiedStorage.saveToIndexedDB('nodes', nodes);
            unifiedStorage.saveToIndexedDB('connections', connections);
            unifiedStorage.saveToIndexedDB('groups', persistedGroups);
            if (isInEditor && currentProjectId) {
                const cvState = useCanvasStore.getState();
                const viewport = { x: cvState.x, y: cvState.y, zoom: cvState.zoom };
                unifiedStorage.saveProjectCache(currentProjectId, { nodes, connections, groups: persistedGroups, viewport });
                unifiedStorage.saveToBackend(currentProjectId, { nodes, connections, groups: persistedGroups });
                unifiedStorage.saveCanvas(currentProjectId, {
                    nodes,
                    edges: connections,
                    groups: persistedGroups,
                    viewport: { ...viewport, updatedAt: Date.now() },
                });
            }
            saveTimerRef.current = null;
        };

        if (isDraggingRef.current) {
            saveTimerRef.current = setTimeout(doSave, 2000);
        } else {
            saveTimerRef.current = setTimeout(doSave, 500);
        }

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
        };
    }, [assetHistory, workflows, nodes, connections, isLoaded, dataLoadSucceeded, isInEditor, currentProjectId]);

    useEffect(() => {
        if (!isLoaded || !isInEditor || !currentProjectId) return;
        unifiedStorage.startAutoSave(
            currentProjectId,
            () => {
                const cvState = useCanvasStore.getState();
                return {
                    nodes: nodesRef.current,
                    connections: connectionsRef.current,
                    groups: persistedGroups,
                    viewport: { x: cvState.x, y: cvState.y, zoom: cvState.zoom },
                };
            },
            getThumbnail
        );
        return () => unifiedStorage.stopAutoSave();
    }, [isLoaded, isInEditor, currentProjectId, getThumbnail]);

    return { loadDataFromStorage };
}
